import assetsBase from '@exodus/assets-base'
import { create } from 'superstruct'
import { BuyOrder } from '../schema/order.js'
import { Quote } from '../schema/quote.js'
import { createPaymentMethodValidator } from '../payment-method.js'
import { PROVIDERS } from '../constants.js'
import {
  DEFAULT_FIAT_PRECISION,
  ENABLED_ASSETS,
  ENABLED_NON_CHAIN_ASSETS,
  PAYMENT_METHODS,
} from './constants.js'

const assets = assetsBase.default || assetsBase

const fromExodusToProviderAsset = (asset, assets_ = ENABLED_ASSETS) =>
  Object.fromEntries(Object.entries(assets_).map((entry) => entry.reverse()))[asset]

const fromProviderToExodusAsset = (asset, assets_ = ENABLED_ASSETS) => {
  const [symbol, chain] = asset?.split('_').reverse() || []
  const assetWithChain = chain ? asset : ENABLED_NON_CHAIN_ASSETS[symbol]

  return assets[assets_[assetWithChain]]
}

const fromExodusToProviderPaymentMethod = (paymentMethod, parameters = {}) => {
  const providerPaymentMethod = [...PAYMENT_METHODS.values()].find(
    createPaymentMethodValidator({ ...parameters, type: paymentMethod })
  )

  return providerPaymentMethod?.id
}

const fromProviderToExodusPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    throw new Error('Payment method is required')
  }

  const type = PAYMENT_METHODS.get(paymentMethod)?.type
  return Array.isArray(type) ? type[0] : type
}

const fromProviderToExodusOrder = (order = {}, assets_ = ENABLED_ASSETS) => {
  const asset = fromProviderToExodusAsset(order.asset?.symbol, assets_)

  return create(
    {
      createdAt: new Date(order.createdAt),
      extraFee: 0,
      fee: order.baseRampFee,
      from: {
        amount: order.fiatValue,
        asset: order.fiatCurrency,
      },
      id: order.id,
      networkFee: order.networkFee,
      paymentMethod: order.paymentMethodType,
      paymentMethodType: order.paymentMethodType
        ? fromProviderToExodusPaymentMethod(order.paymentMethodType)
        : undefined,
      provider: PROVIDERS.ramp,
      providerId: order.id,
      rate: order.assetExchangeRate,
      status: order.status,
      to: {
        amount: asset?.currency.baseUnit(order.cryptoAmount).toDefaultNumber(),
        asset: asset?.name,
      },
      totalFee: order.appliedFee,
      txId: order.finalTxHash,
      updatedAt: new Date(order.updatedAt),
      walletAddress: order.receiverAddress,
    },
    BuyOrder
  )
}

const fromProviderToExodusQuote = (quote = {}) => {
  const asset = assets[quote.toAsset]
  const {
    appliedFee: totalFeeFiat,
    baseRampFee,
    cryptoAmount: cryptoBaseAmount,
    fiatCurrency,
    fiatValue: fiatAmount,
  } = quote[quote.paymentMethod] || {}

  const cryptoAmount = asset?.currency.baseUnit(cryptoBaseAmount).toDefaultNumber()
  const totalFeeCrypto = Number.isNaN(Number(totalFeeFiat))
    ? undefined
    : (cryptoAmount * totalFeeFiat) / fiatAmount

  return create(
    {
      extraFee: 0,
      fee: baseRampFee,
      from: {
        amount: fiatAmount,
        asset: quote.fromAsset,
        maxBuyAmount: quote.fromMaxBuyAmount,
        maxSellAmount: quote.fromMaxSellAmount,
        minBuyAmount: quote.fromMinBuyAmount,
        minSellAmount: quote.fromMinSellAmount,
        precision: DEFAULT_FIAT_PRECISION,
      },
      networkFee: quote.asset.networkFee,
      paymentMethod: quote.paymentMethod,
      provider: PROVIDERS.ramp,
      rate: quote.asset.price?.[fiatCurrency],
      to: {
        amount: cryptoAmount,
        asset: quote.toAsset,
        maxBuyAmount: quote.toMaxBuyAmount,
        maxSellAmount: quote.toMaxSellAmount,
        minBuyAmount: quote.toMinBuyAmount,
        minSellAmount: quote.toMinSellAmount,
        precision: quote.asset.decimals,
      },
      totalAmount: fiatAmount,
      totalFee: totalFeeFiat,
      totalFeeCrypto,
      type: quote.type,
    },
    Quote
  )
}

const rampAdapter = {
  fromExodusToProviderAsset,
  fromExodusToProviderPaymentMethod,
  fromProviderToExodusAsset,
  fromProviderToExodusOrder,
  fromProviderToExodusPaymentMethod,
  fromProviderToExodusQuote,
}

export default rampAdapter
